import { gql as gqlQuery, useMutation } from '@apollo/client';
import {
  DataExportStates,
  type FetchApiExportResponse,
} from '@generated/graphql';
import {
  Dialog,
  DialogContent,
  DialogTitle,
} from '@ovComponents/2-component/dialog/dialog';
import { LocalizedDatePicker } from '@ovComponents/2-component/localizedDatePicker/localizedDatePicker';
import { TextField } from '@ovComponents/2-component/textField/textField';
import {
  EXPORT_COMPLETION_OPEN_DELAY_MS,
  EXPORT_PROGRESS_DECIMAL_PLACES,
  EXPORT_PROGRESS_PERCENT_SCALE,
} from '@ovComponents/3-pattern/exportPoll/constants';
import { createExportPoll } from '@ovComponents/3-pattern/exportPoll/createExportPoll';
import { formatPercentValue } from '@utils/moneyFormatting';
import { safeWindowOpen } from '@utils/urlSanitization';
import dayjs from 'dayjs';
import {
  Box,
  IconButton,
  Tooltip,
  Typography,
  useTheme,
  useToast,
} from 'hephaestus/web';
import {
  Clear as ClearIcon,
  Close as CloseIcon,
  FileDownloadOutlined as FileDownloadOutlinedIcon,
} from 'hephaestus/webIcons';
import { useCallback, useEffect, useRef, useState } from 'react';
import { useTranslation } from 'react-i18next';
import ConfigurationModal, { type Configuration } from './configurationModal';

const FETCH_API_EXPORT = gqlQuery`
  query fetchApiExport($input: FetchApiExportInput!) {
    fetchApiExport(input: $input) {
      apiExport {
        id
        progress
        state
        fileUrl
      }
    }
  }
`;

const API_EXPORT_STATES = {
  generating: DataExportStates.Generating,
  completed: DataExportStates.Completed,
  failed: DataExportStates.Failed,
} as const;

type FetchApiExportPollData = {
  fetchApiExport?: FetchApiExportResponse;
};

const selectApiExport = (data: FetchApiExportPollData | undefined) =>
  data?.fetchApiExport?.apiExport;

const useApiExportPoll = createExportPoll<
  FetchApiExportPollData,
  { input: { apiExportId: string } }
>({
  query: FETCH_API_EXPORT,
  selectExport: selectApiExport,
  states: API_EXPORT_STATES,
});

export enum ExportApiReportFormatters {
  DIVIDE_BY_100 = 'DIVIDE_BY_100',
  MULTIPLY_BY_100 = 'MULTIPLY_BY_100',
}

export type Column = {
  gqlAlias: string;
  header: string;
  formatter?: ExportApiReportFormatters;
  isCustomField?: boolean;
};

export type DateRangeConfig = {
  fromLabel?: string;
  toLabel?: string;
  fromDate: string | null;
  toDate: string | null;
  onFromDateChange: (date: string | null) => void;
  onToDateChange: (date: string | null) => void;
};

export type DownloadButtonProps = {
  columns: Column[];
  additionalColumns?: Column[];
  datasetPath: string;
  fileName?: string;
  filter: Record<string, any>;
  additionalFilter?: Record<string, any>;
  gql: string;
  queryPath: string;
  sortDesc?: boolean;
  sortField?: string;
  perPage?: number;
  progressTitle?: string;
  configurable?: boolean;
  testId?: string;
  dateRangeConfig?: DateRangeConfig;
  maxDateWindow?: number;
  onDateValidationError?: (message: string) => void;
  disabled?: boolean;
  onColumnsChange?: (columns: string[]) => void;
  onExportComplete?: () => void;
  onExportStart?: () => void;
};

const EXPORT_API_REPORT = gqlQuery`
  mutation exportApiReport($input: ExportApiReportInput!) {
    exportApiReport(input: $input) {
      apiExport {
        id
      }
    }
  }
`;

/**
 * @param columns - use it for columns that are usually present in a table and visible to a user
 * @param additionalColumns - use it for additional columns to fetch the data beyond the table columns
 * @param filter - use it for required filters ex. user, organization
 * @param additionalFilter - use it for optional filters ex. state, type
 * @param configurable - use it for showing a configuration dialog before exporting, a user will be able to choose desired columns to export
 */
export const DownloadButton = ({
  columns,
  additionalColumns,
  datasetPath,
  fileName,
  filter,
  additionalFilter,
  gql,
  queryPath,
  sortDesc,
  sortField,
  perPage,
  progressTitle,
  configurable,
  testId,
  dateRangeConfig,
  maxDateWindow,
  onDateValidationError,
  disabled,
  onColumnsChange,
  onExportComplete,
  onExportStart,
}: DownloadButtonProps) => {
  const [openConfiguration, setOpenConfiguration] = useState(false);
  const [activeExportId, setActiveExportId] = useState<string | null>(null);
  const { t } = useTranslation(['components', 'shared']);
  const theme = useTheme();
  const { enqueueToast } = useToast();
  const completionTimerRef = useRef<ReturnType<typeof setTimeout> | null>(null);

  const cancelCompletionTimer = useCallback(() => {
    if (completionTimerRef.current !== null) {
      clearTimeout(completionTimerRef.current);
      completionTimerRef.current = null;
    }
  }, []);

  useEffect(() => () => cancelCompletionTimer(), [cancelCompletionTimer]);

  const finishExport = useCallback(
    (fileUrl: string) => {
      setActiveExportId(null);
      cancelCompletionTimer();
      completionTimerRef.current = setTimeout(() => {
        completionTimerRef.current = null;
        safeWindowOpen(fileUrl, {
          target: '_blank',
          onError: (errorMsg) => {
            enqueueToast(errorMsg, { variant: 'error' });
          },
        });
        onExportComplete?.();
      }, EXPORT_COMPLETION_OPEN_DELAY_MS);
    },
    [cancelCompletionTimer, onExportComplete, enqueueToast],
  );

  // No cancelCompletionTimer() here: the completion timer is only ever set
  // by finishExport (a successful export), and the poll's success/failure
  // callbacks are mutually exclusive (see useExportPoll's handledRef guard),
  // so a pending timer can never be running when this fires.
  const failExport = useCallback(() => {
    enqueueToast(t('downloadButton.failedDownloadMessage'), {
      variant: 'error',
    });
    setActiveExportId(null);
  }, [enqueueToast, t]);

  const [exportApiReport] = useMutation(EXPORT_API_REPORT);

  const { progress } = useApiExportPoll({
    variables: { input: { apiExportId: activeExportId ?? '' } },
    enabled: activeExportId !== null,
    onComplete: finishExport,
    onFail: failExport,
  });

  // No cancelCompletionTimer() here either: the dialog already closes
  // (activeExportId -> null) synchronously in finishExport/failExport before
  // the completion timer exists, so this can't be clicked while one is
  // pending.
  const dismissProgressDialog = useCallback(() => {
    setActiveExportId(null);
  }, []);

  const onExport = useCallback(
    async (configuration?: Configuration) => {
      cancelCompletionTimer();
      setActiveExportId(null);
      onExportStart?.();

      try {
        const { data } = await exportApiReport({
          variables: {
            input: {
              gql,
              filter: configuration?.useAdditionalFilter
                ? { ...filter, ...additionalFilter }
                : filter,
              queryPath,
              datasetPath,
              columns: configuration?.selectedColumns ?? columns,
              fileName,
              sortField,
              sortDesc,
              perPage,
            },
          },
        });

        const apiExportId = data?.exportApiReport?.apiExport?.id;
        if (!apiExportId) {
          failExport();
          return;
        }

        setActiveExportId(apiExportId);
      } catch {
        failExport();
      }
    },
    [
      additionalFilter,
      cancelCompletionTimer,
      columns,
      datasetPath,
      exportApiReport,
      failExport,
      fileName,
      filter,
      gql,
      onExportStart,
      perPage,
      queryPath,
      sortDesc,
      sortField,
    ],
  );

  const handleDateChange = (
    date: Date,
    onDateChange: (date: string | null) => void,
  ) => {
    const formattedDate = dayjs(date).format('YYYY-MM-DD');
    onDateChange(formattedDate);
  };

  const renderDateRangePickers = () => {
    if (!dateRangeConfig) return null;

    return (
      <Box>
        <Box
          sx={{
            display: 'flex',
            flexDirection: 'column',
            gap: 2,
            mt: 1,
          }}
        >
          <LocalizedDatePicker
            value={dateRangeConfig.fromDate || null}
            onChange={(date: Date) =>
              handleDateChange(date, dateRangeConfig.onFromDateChange)
            }
            onAccept={(date: Date) =>
              handleDateChange(date, dateRangeConfig.onFromDateChange)
            }
            renderInput={(params: any) => (
              <TextField
                fullWidth
                {...params}
                type='default'
                label={
                  dateRangeConfig.fromLabel || t('exportConfiguration.fromDate')
                }
                trailingIcon='calendar'
              />
            )}
            InputProps={{
              endAdornment: dateRangeConfig.fromDate && (
                <IconButton
                  aria-label='clear'
                  onClick={() => dateRangeConfig.onFromDateChange(null)}
                >
                  <ClearIcon fontSize='inherit' />
                </IconButton>
              ),
            }}
          />

          <LocalizedDatePicker
            value={dateRangeConfig.toDate || null}
            onChange={(date: Date) =>
              handleDateChange(date, dateRangeConfig.onToDateChange)
            }
            onAccept={(date: Date) =>
              handleDateChange(date, dateRangeConfig.onToDateChange)
            }
            renderInput={(params: any) => (
              <TextField
                fullWidth
                {...params}
                type='default'
                label={
                  dateRangeConfig.toLabel || t('exportConfiguration.toDate')
                }
                trailingIcon='calendar'
              />
            )}
            InputProps={{
              endAdornment: dateRangeConfig.toDate && (
                <IconButton
                  aria-label='clear'
                  onClick={() => dateRangeConfig.onToDateChange(null)}
                >
                  <ClearIcon fontSize='inherit' />
                </IconButton>
              ),
            }}
          />

          {maxDateWindow && (
            <Typography
              variant='bodyMedium'
              sx={{ color: 'gray', fontSize: '12px', fontWeight: 500 }}
            >
              {t('exportConfiguration.maxDateRange', { days: maxDateWindow })}
            </Typography>
          )}
        </Box>
      </Box>
    );
  };

  return (
    <>
      <Tooltip title={String(t('downloadButton.download'))}>
        <span>
          <IconButton
            onClick={() =>
              configurable ? setOpenConfiguration(true) : void onExport()
            }
            data-testid={testId ?? 'download-button'}
            disabled={disabled}
            aria-label={String(t('downloadButton.download'))}
          >
            <FileDownloadOutlinedIcon fontSize='inherit' />
          </IconButton>
        </span>
      </Tooltip>

      <ConfigurationModal
        open={openConfiguration}
        onClose={() => setOpenConfiguration(false)}
        onExport={(configuration) => {
          void onExport(configuration);
          setOpenConfiguration(false);
        }}
        columns={columns}
        additionalColumns={additionalColumns}
        maxDateWindow={maxDateWindow}
        onDateValidationError={onDateValidationError}
        dateRangeConfig={dateRangeConfig}
        onColumnsChange={onColumnsChange}
      >
        {renderDateRangePickers()}
      </ConfigurationModal>

      <Dialog
        onClose={dismissProgressDialog}
        open={activeExportId !== null}
        fullWidth
        maxWidth='xs'
      >
        <DialogTitle>
          <Box
            sx={{
              display: 'flex',
              justifyContent: 'space-between',
              alignItems: 'center',
            }}
          >
            {progressTitle ?? t('downloadButton.progressModalTitle')}
            <Tooltip title={String(t('shared:close'))}>
              <IconButton
                onClick={dismissProgressDialog}
                data-testid='download-progress-dialog-close-button'
                sx={{ color: `${theme.color.onSurface}` }}
                aria-label={String(t('shared:close'))}
              >
                <CloseIcon fontSize='inherit' />
              </IconButton>
            </Tooltip>
          </Box>
        </DialogTitle>
        <DialogContent>
          <Box
            sx={{
              display: 'flex',
              justifyContent: 'space-between',
              alignItems: 'center',
            }}
          >
            <Box
              sx={{
                position: 'relative',
                height: '8px',
                borderRadius: '4px',
                backgroundColor: `${theme.color.surfaceContainerVariant}`,
                width: '90%',
              }}
            >
              <Box
                sx={{
                  position: 'relative',
                  height: '8px',
                  borderRadius: '4px',
                  backgroundColor: `${theme.color.primary}`,
                  transition: 'width 0.5s',
                  width: `${progress * EXPORT_PROGRESS_PERCENT_SCALE}%`,
                }}
              />
            </Box>
            <Typography
              variant='bodySmall'
              component='p'
              sx={{ fontWeight: 500 }}
            >
              {formatPercentValue(progress, EXPORT_PROGRESS_DECIMAL_PLACES)}
            </Typography>
          </Box>
        </DialogContent>
      </Dialog>
    </>
  );
};
